import { getCoreApiUrl } from '../config/deploymentConfig';
import { logError } from './LoggingService';

/**
 * JWT Authentication Service for On-Premises Deployment
 *
 * Handles authentication using JWT tokens with the Core API.
 * Tokens are stored in localStorage and memory.
 */

// Token storage keys
const ACCESS_TOKEN_KEY = 'membrowse_access_token';
const REFRESH_TOKEN_KEY = 'membrowse_refresh_token';
const USER_KEY = 'membrowse_user';

// In-memory token storage (for security)
let accessTokenMemory = null;
let refreshTokenMemory = null;

// Deduplication: single in-flight refresh promise shared across concurrent callers
let pendingRefreshPromise = null;

/**
 * Store tokens securely
 * @param {string} accessToken - JWT access token
 * @param {string} refreshToken - JWT refresh token
 * @param {Object} user - User object
 */
const storeTokens = (accessToken, refreshToken, user) => {
  // Store in memory
  accessTokenMemory = accessToken;
  refreshTokenMemory = refreshToken;

  // Store tokens in localStorage (httpOnly cookies would be better but requires server setup)
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

/**
 * Decode JWT payload and check if the token is expired or about to expire.
 * Uses a 30-second buffer to avoid using a token that will expire mid-request.
 * @param {string} token - JWT token string
 * @returns {boolean} true if expired or will expire within 30 seconds
 */
const isTokenExpired = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    // Convert base64url to standard base64 before decoding
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    const payload = JSON.parse(atob(base64));
    if (!payload.exp) return true;
    const nowSeconds = Math.floor(Date.now() / 1000);
    return payload.exp <= nowSeconds + 30;
  } catch {
    return true;
  }
};

/**
 * Get stored access token
 * @returns {string|null}
 */
export const getAccessToken = () => {
  return accessTokenMemory;
};

/**
 * Get stored refresh token
 * @returns {string|null}
 */
const getRefreshToken = () => {
  return refreshTokenMemory || localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Clear stored tokens
 */
const clearTokens = () => {
  accessTokenMemory = null;
  refreshTokenMemory = null;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

/**
 * Make authenticated API request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>}
 */
// eslint-disable-next-line no-unused-vars
const apiRequest = async (endpoint, options = {}) => {
  const url = `${getCoreApiUrl()}${endpoint}`;
  const token = getAccessToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return fetch(url, {
    ...options,
    headers
  });
};

/**
 * Get current session.
 * Checks token expiry client-side and proactively refreshes if expired,
 * avoiding unnecessary 401 round-trips to the backend.
 * @returns {Promise<Object|null>}
 */
export const getSession = async () => {
  try {
    const token = getAccessToken();
    const userStr = localStorage.getItem(USER_KEY);

    if (!token || !userStr) {
      return null;
    }

    // Check if the access token is expired or about to expire
    if (isTokenExpired(token)) {
      // Proactively refresh instead of sending an expired token to the backend
      const refreshed = await refreshSession();
      return refreshed;
    }

    const user = JSON.parse(userStr);
    return {
      user,
      access_token: token
    };
  } catch (error) {
    logError('Failed to get session', error, 'JWTAuthService');
    return null;
  }
};

/**
 * Internal refresh implementation (not deduplicated).
 * @returns {Promise<Object|null>}
 */
const doRefreshSession = async () => {
  try {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      return null;
    }

    const response = await fetch(`${getCoreApiUrl()}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!response.ok) {
      clearTokens();
      return null;
    }

    const result = await response.json();

    if (result.success && result.data) {
      const { access_token } = result.data;
      const userStr = localStorage.getItem(USER_KEY);
      const user = userStr ? JSON.parse(userStr) : null;

      // Update access token in memory AND localStorage to stay in sync
      accessTokenMemory = access_token;
      localStorage.setItem(ACCESS_TOKEN_KEY, access_token);

      return {
        user,
        access_token
      };
    }

    return null;
  } catch (error) {
    logError('Failed to refresh session', error, 'JWTAuthService');
    clearTokens();
    return null;
  }
};

/**
 * Refresh session using refresh token.
 * Deduplicated: concurrent callers share a single in-flight refresh request
 * to avoid hammering the backend with multiple /auth/refresh calls.
 * @returns {Promise<Object|null>}
 */
export const refreshSession = async () => {
  if (pendingRefreshPromise) {
    return pendingRefreshPromise;
  }

  pendingRefreshPromise = doRefreshSession().finally(() => {
    pendingRefreshPromise = null;
  });

  return pendingRefreshPromise;
};

/**
 * Sign in with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>}
 */
export const signIn = async (email, password) => {
  try {
    const response = await fetch(`${getCoreApiUrl()}/auth/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        data: null,
        error: { message: result.error || 'Login failed' }
      };
    }

    if (result.success && result.data) {
      const { user, access_token, refresh_token } = result.data;

      storeTokens(access_token, refresh_token, user);

      return {
        data: {
          user: {
            ...user,
            access_token
          },
          session: {
            user,
            access_token
          }
        },
        error: null
      };
    }

    return {
      data: null,
      error: { message: 'Invalid response from server' }
    };
  } catch (error) {
    logError('Sign in failed', error, 'JWTAuthService');
    return {
      data: null,
      error: { message: error.message || 'Sign in failed' }
    };
  }
};

/**
 * Sign up with email and password
 * @param {string} email
 * @param {string} password
 * @param {Object} metadata - User metadata (e.g., full_name)
 * @returns {Promise<Object>}
 */
export const signUp = async (email, password, metadata = {}) => {
  try {
    const response = await fetch(`${getCoreApiUrl()}/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password, metadata })
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        data: null,
        error: { message: result.error || 'Signup failed' }
      };
    }

    if (result.success && result.data) {
      const { user, access_token, refresh_token } = result.data;

      storeTokens(access_token, refresh_token, user);

      return {
        data: {
          user: {
            ...user,
            access_token
          },
          session: {
            user,
            access_token
          }
        },
        error: null
      };
    }

    return {
      data: null,
      error: { message: 'Invalid response from server' }
    };
  } catch (error) {
    logError('Sign up failed', error, 'JWTAuthService');
    return {
      data: null,
      error: { message: error.message || 'Sign up failed' }
    };
  }
};

/**
 * Sign out current user
 * @returns {Promise<Object>}
 */
export const signOut = async () => {
  try {
    const refreshToken = getRefreshToken();

    if (refreshToken) {
      // Attempt to invalidate refresh token on server
      await fetch(`${getCoreApiUrl()}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
    }

    clearTokens();

    return { error: null };
  } catch (error) {
    logError('Sign out failed', error, 'JWTAuthService');
    clearTokens();
    return { error: null }; // Always succeed client-side
  }
};

/**
 * Sign in with OAuth provider (not supported in on-prem)
 * @returns {Promise<Object>}
 */
export const signInWithOAuth = async (provider) => {
  return {
    data: null,
    error: { message: 'OAuth not supported in on-premises deployment' }
  };
};

/**
 * Reset password (not implemented in on-prem yet)
 * @returns {Promise<Object>}
 */
export const resetPassword = async (email) => {
  return {
    data: null,
    error: { message: 'Password reset not yet implemented for on-premises deployment' }
  };
};

/**
 * Update password (not implemented in on-prem yet)
 * @returns {Promise<Object>}
 */
export const updatePassword = async (password) => {
  return {
    data: null,
    error: { message: 'Password update not yet implemented for on-premises deployment' }
  };
};

/**
 * Auth state change subscription (simplified for JWT)
 * @param {Function} callback
 * @returns {Object} Subscription object with unsubscribe method
 */
export const onAuthStateChange = (callback) => {
  // For JWT auth, we don't have real-time state changes
  // Return a dummy subscription
  return {
    unsubscribe: () => {}
  };
};

// Initialize tokens from localStorage on load
const initializeTokens = () => {
  // Restore tokens from localStorage
  accessTokenMemory = localStorage.getItem(ACCESS_TOKEN_KEY);
  refreshTokenMemory = localStorage.getItem(REFRESH_TOKEN_KEY);

  // Note: Session validation is removed to prevent redundant /auth/me calls
  // AuthContext will handle session initialization when the app loads
};

// Initialize on module load
initializeTokens();
